import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistorique } from '../lib/historique'
import { supabase } from '../lib/supabase'
import { useAuth, peutAjouterPhotos } from '../lib/auth'
import { preparerImage, dateExif } from '../lib/images'
import { mettreEnAttente, fileDAttente } from '../lib/db'
import { synchroniser, surChangementFile } from '../lib/sync'
import { cleJour, dateLongue, horodatageCourt } from '../lib/format'
import type { Chantier, OrigineDate, Photo, PhotoEnAttente } from '../lib/types'
import { CameraRafale } from './CameraRafale'
import { FichePhoto } from './FichePhoto'
import { Superpose } from '../components/Superpose'

type Entree =
  | { genre: 'envoyee'; photo: Photo; url: string | null }
  | { genre: 'attente'; photo: PhotoEnAttente; url: string }

export function Photos({ chantier, ouvrirCamera, setOuvrirCamera, creerDeficience }: {
  chantier: Chantier
  ouvrirCamera: boolean
  setOuvrirCamera: (v: boolean) => void
  /** « Créer une déficience à partir de cette photo » (fiche photo). */
  creerDeficience?: (photoClientUuid: string) => void
}) {
  const { profil } = useAuth()
  const [envoyees, setEnvoyees] = useState<Photo[]>([])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [attente, setAttente] = useState<PhotoEnAttente[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)
  const [selection, setSelection] = useState<Entree | null>(null)
  useHistorique(selection, setSelection, (e) => e !== null) // défiler entre les photos ne compte pas
  const [demandeDate, setDemandeDate] = useState<{ blobs: Blob[]; index: number } | null>(null)
  const champFichier = useRef<HTMLInputElement>(null)
  const ecrivable = peutAjouterPhotos(profil, chantier.statut)

  const rafraichirAttente = useCallback(async () => {
    setAttente(await fileDAttente(chantier.id))
  }, [chantier.id])

  useEffect(() => surChangementFile(() => void rafraichirAttente()), [rafraichirAttente])

  const charger = useCallback(async () => {
    setChargement(true)
    const { data, error } = await supabase
      .from('photos')
      .select('*')
      .eq('chantier_id', chantier.id)
      .order('prise_le', { ascending: false })
      .limit(300)
    if (error) {
      setErreur(error.message)
      setChargement(false)
      return
    }
    const liste = (data as Photo[]) ?? []
    setEnvoyees(liste)
    setChargement(false)

    if (liste.length > 0) {
      const { data: signees } = await supabase.storage
        .from('photos')
        .createSignedUrls(liste.map((p) => p.fichier_path), 60 * 60)
      const carte: Record<string, string> = {}
      signees?.forEach((s) => {
        if (s.signedUrl && s.path) carte[s.path] = s.signedUrl
      })
      setUrls(carte)
    }
  }, [chantier.id])

  useEffect(() => {
    void charger()
    void rafraichirAttente()
  }, [charger, rafraichirAttente])

  /** Enregistre des images dans la file d'attente, puis tente l'envoi. */
  async function enregistrer(blobs: Blob[], origine: OrigineDate, dateForcee?: Date) {
    setErreur(null)
    try {
      for (const brut of blobs) {
        const { blob, largeur, hauteur } = await preparerImage(brut)
        const quand =
          dateForcee ?? (origine === 'exif' ? (await dateExif(brut)) ?? new Date() : new Date())
        await mettreEnAttente({
          client_uuid: crypto.randomUUID(),
          chantier_id: chantier.id,
          prise_le: quand.toISOString(),
          source: origine === 'appareil' ? 'appareil' : 'televersee',
          origine_date: origine,
          note: null,
          blob,
          largeur,
          hauteur,
          cree_le: Date.now(),
          essais: 0
        })
      }
      await rafraichirAttente()
      void synchroniser().then(() => void charger())
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    }
  }

  /** Téléversement : on lit la date EXIF, et on la demande si elle manque. */
  async function televersement(liste: FileList | null) {
    if (!liste || liste.length === 0) return
    const fichiers = Array.from(liste)
    const sansDate: Blob[] = []
    const avecDate: Blob[] = []
    for (const f of fichiers) {
      const d = await dateExif(f)
      if (d) avecDate.push(f)
      else sansDate.push(f)
    }
    if (avecDate.length > 0) await enregistrer(avecDate, 'exif')
    if (sansDate.length > 0) setDemandeDate({ blobs: sansDate, index: 0 })
    if (champFichier.current) champFichier.current.value = ''
  }

  // Une URL d'objet par photo en attente, révoquée quand la file change :
  // sans cela, chaque rendu en créerait une nouvelle sans jamais la libérer.
  const urlsLocales = useMemo(
    () => attente.map((p) => ({ uuid: p.client_uuid, url: URL.createObjectURL(p.blob) })),
    [attente]
  )
  useEffect(
    () => () => urlsLocales.forEach((u) => URL.revokeObjectURL(u.url)),
    [urlsLocales]
  )

  const entrees: Entree[] = useMemo(
    () => [
      ...attente.map((p, i) => ({
        genre: 'attente' as const,
        photo: p,
        url: urlsLocales[i]?.url ?? ''
      })),
      ...envoyees.map((p) => ({
        genre: 'envoyee' as const,
        photo: p,
        url: urls[p.fichier_path] ?? null
      }))
    ],
    [attente, envoyees, urls, urlsLocales]
  )

  const groupes = new Map<string, Entree[]>()
  for (const e of entrees) {
    const k = cleJour(e.photo.prise_le)
    if (!groupes.has(k)) groupes.set(k, [])
    groupes.get(k)!.push(e)
  }
  const jours = [...groupes.keys()].sort().reverse()
  // Ordre exact de la grille (jour le plus récent en premier) : c'est l'ordre
  // de défilement quand une photo est ouverte.
  const ordre = jours.flatMap((j) => groupes.get(j) ?? [])
  const cleDe = (e: Entree) => (e.genre === 'attente' ? e.photo.client_uuid : e.photo.id)
  const position = selection ? ordre.findIndex((e) => cleDe(e) === cleDe(selection)) : -1
  const aller = (delta: number) => {
    const cible = ordre[position + delta]
    if (cible) setSelection(cible)
  }

  return (
    <>
      {ouvrirCamera && (
        <CameraRafale
          fermer={() => setOuvrirCamera(false)}
          capturer={(blobs) => {
            setOuvrirCamera(false)
            void enregistrer(blobs, 'appareil')
          }}
        />
      )}

      {selection && (
        <FichePhoto
          entree={ordre[position] ?? selection}
          chantier={chantier}
          position={position >= 0 ? { rang: position + 1, total: ordre.length } : undefined}
          precedente={position > 0 ? () => aller(-1) : undefined}
          suivante={position >= 0 && position < ordre.length - 1 ? () => aller(1) : undefined}
          fermer={() => setSelection(null)}
          creerDeficience={
            creerDeficience
              ? (uuid) => {
                  setSelection(null)
                  creerDeficience(uuid)
                }
              : undefined
          }
          modifiee={() => {
            setSelection(null)
            void charger()
            void rafraichirAttente()
          }}
        />
      )}

      {demandeDate && (
        <DemandeDate
          nombre={demandeDate.blobs.length}
          annuler={() => setDemandeDate(null)}
          valider={(d) => {
            const blobs = demandeDate.blobs
            setDemandeDate(null)
            void enregistrer(blobs, 'manuelle', d)
          }}
        />
      )}

      <input
        ref={champFichier}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => void televersement(e.target.files)}
      />

      {erreur && <p className="avis erreur" style={{ marginBottom: 12 }}>{erreur}</p>}

      {ecrivable && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button className="bouton" onClick={() => setOuvrirCamera(true)}>
            Appareil photo
          </button>
          <button className="bouton creux" onClick={() => champFichier.current?.click()}>
            Téléverser
          </button>
        </div>
      )}

      {chargement && entrees.length === 0 && <p className="vide">Chargement…</p>}

      {!chargement && entrees.length === 0 && (
        <p className="vide">
          Aucune photo sur ce chantier.
          {ecrivable && (
            <>
              <br />
              L'appareil reste ouvert entre les prises : on peut enchaîner une série.
            </>
          )}
        </p>
      )}

      {jours.map((j) => (
        <div key={j}>
          <p className="jour">{dateLongue(groupes.get(j)![0].photo.prise_le)}</p>
          <div className="grille">
            {groupes.get(j)!.map((e) => (
              <Tuile
                key={e.genre === 'attente' ? e.photo.client_uuid : e.photo.id}
                entree={e}
                ouvrir={() => setSelection(e)}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

function Tuile({ entree, ouvrir }: { entree: Entree; ouvrir: () => void }) {
  const enAttente = entree.genre === 'attente'
  const source = entree.photo.source
  return (
    <button className="tuile" onClick={ouvrir}>
      {entree.url ? (
        <img src={entree.url} alt="" loading="lazy" />
      ) : (
        <span style={{ display: 'block', width: '100%', height: '100%' }} />
      )}
      <span className={`prov ${enAttente ? 'file' : source === 'televersee' ? 'tel' : ''}`}>
        {enAttente ? 'EN FILE' : source === 'televersee' ? 'TÉLÉV.' : 'APP'}
      </span>
      <span className="horo">{horodatageCourt(entree.photo.prise_le)}</span>
    </button>
  )
}

function DemandeDate({
  nombre,
  valider,
  annuler
}: {
  nombre: number
  valider: (d: Date) => void
  annuler: () => void
}) {
  const [valeur, setValeur] = useState(() => {
    const d = new Date()
    const n = (x: number) => String(x).padStart(2, '0')
    return `${d.getFullYear()}-${n(d.getMonth() + 1)}-${n(d.getDate())}T${n(d.getHours())}:${n(d.getMinutes())}`
  })

  return (
    <Superpose>
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={annuler}>
            ‹ Annuler
          </button>
          <h1>Date de la photo</h1>
        </div>
      </header>
      <div className="corps">
        <p className="avis" style={{ marginBottom: 16 }}>
          {nombre === 1 ? 'Cette photo ne porte pas' : `Ces ${nombre} photos ne portent pas`} de date
          de prise de vue. C'est fréquent : les messageries et les captures d'écran effacent cette
          information. Indique la date réelle — c'est elle qui donnera sa valeur à la photo si un
          litige survient plus tard.
        </p>
        <label className="champ">
          <span>Date et heure de la prise</span>
          <input
            type="datetime-local"
            value={valeur}
            onChange={(e) => setValeur(e.target.value)}
          />
        </label>
      </div>
      <div className="actions">
        <button className="bouton" onClick={() => valider(new Date(valeur))}>
          Enregistrer
        </button>
      </div>
    </Superpose>
  )
}
